import React, { useState, useRef, useEffect, useCallback } from "react";
import { Y, T, CREDIT, BG_SRC, inp } from "../theme";
import { auth } from "../firebase";

/* ── Resolution color theme ──────────────────────────────────────────────── */
export const RES_COLORS = {
  "360p":  "#88CC44",  // yellow-green (draft tier, below 480p)
  "480p":  "#22DD88",  // bright cool green
  "720p":  "#2277FF",  // bright cobalt blue
  "1080p": "#AA44FF",  // bright purple
  "1440p": "#FFB81C",  // saturated gold (legacy alias for 2K)
  "2K":    "#FFB81C",  // 2K shares the 1440p color (video context only)
  "2160p": "#FF4422",  // orange-red
  "4K":    "#FF4422",  // orange-red (video 4K = 2160p)
  // Image-context aliases - shifted up one tier visually so 1K reads
  // as "small/typical" (blue), 2K as "premium" (purple), 4K as the
  // top tier (gold). Distinct keys so video "4K" can keep its
  // orange-red. resLabel below maps these internal tokens back to
  // clean display labels ("1K"/"2K"/"4K").
  "Img1K": "#2277FF",  // blue (matches 720p video)
  "Img2K": "#AA44FF",  // purple (matches 1080p video)
  "Img4K": "#FFB81C",  // gold (matches 1440p video)
  // Vector / resolution-independent output. Dimmed cyan reads as
  // "different family from the pixel resolutions" without grabbing
  // attention the way the saturated raster colors do.
  "SVG":   "#5DAEB8",
};
const resColor = (r) => RES_COLORS[r] || T.text3;
// Public-facing label for a resolution. Video keys keep their pixel-height
// names (2160p / 1440p / 1080p / 720p / 480p) to differentiate them from
// the image-side "1K / 2K / 4K" naming. Image keys come in with an "Img"
// prefix purely for color lookup; that prefix is stripped here so the pill
// shows just "1K"/"2K"/"4K".
const resLabel = (r) => {
  if (r === '4K')    return '2160p'; // video 4K renders as 2160p
  if (r === 'Img1K') return '1K';
  if (r === 'Img2K') return '2K';
  if (r === 'Img4K') return '4K';
  return r;
};

// Rasterize an SVG blob into a PNG blob via an offscreen canvas. PNG
// preserves transparency (Recraft outputs sometimes have transparent
// backgrounds, and JPG would flatten them to black/white). The maxDim
// cap keeps file size below the 5MB Recraft input limit even for
// busy SVGs - 1536px on the long edge produces a typical 2-4MB PNG.
export async function rasterizeSvgBlobToPng(svgBlob, { maxDim = 1536 } = {}) {
  const url = URL.createObjectURL(svgBlob);
  try {
    // Probe via Image element to read the SVG's natural dims.
    const img = new Image();
    await new Promise((resolve, reject) => {
      img.onload  = resolve;
      img.onerror = () => reject(new Error('Failed to load SVG'));
      img.src = url;
    });
    let w = img.naturalWidth;
    let h = img.naturalHeight;
    // Some SVGs declare only a viewBox with no explicit width/height
    // attrs, leaving naturalWidth/Height at 0 in some browsers. Parse
    // the viewBox out of the first chunk of the file as a fallback.
    if (!w || !h) {
      try {
        const head = await svgBlob.slice(0, 2048).text();
        const m = head.match(/viewBox=["']\s*[\d.-]+\s+[\d.-]+\s+([\d.]+)\s+([\d.]+)\s*["']/i);
        if (m) { w = parseFloat(m[1]); h = parseFloat(m[2]); }
      } catch {}
    }
    if (!w || !h) { w = 1024; h = 1024; }
    const scale = Math.min(1, maxDim / Math.max(w, h));
    const cw = Math.max(256, Math.round(w * scale));
    const ch = Math.max(256, Math.round(h * scale));
    const canvas = document.createElement('canvas');
    canvas.width = cw;
    canvas.height = ch;
    const ctx = canvas.getContext('2d');
    // Don't fill - PNG keeps transparency. drawImage at full canvas size.
    ctx.drawImage(img, 0, 0, cw, ch);
    return await new Promise((resolve, reject) => {
      canvas.toBlob(blob => {
        if (blob) resolve(blob);
        else reject(new Error('Canvas → PNG encoding failed'));
      }, 'image/png');
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

// Detect SVG content from a blob via mime-type or first-256-bytes sniff.
// Object URLs returned by `URL.createObjectURL(fetched_blob)` sometimes
// drop the Content-Type header so a plain mime check isn't enough.
export async function isSvgBlob(blob) {
  const ct = String(blob.type || '').toLowerCase();
  if (ct.includes('svg')) return true;
  try {
    const head = (await blob.slice(0, 256).text()).trim().toLowerCase();
    return head.startsWith('<?xml') || head.startsWith('<svg');
  } catch {
    return false;
  }
}

// Rasterize any image blob (SVG / PNG / JPG / WEBP) to a JPG blob via an
// offscreen canvas. JPG can't store transparency so any alpha-bearing
// pixels (PNG / SVG outputs that have see-through backgrounds) are
// flattened against white. Used by the "Download as JPG" secondary
// download button.
export async function rasterizeBlobToJpg(blob, { maxDim = 2048, quality = 0.9 } = {}) {
  const url = URL.createObjectURL(blob);
  try {
    const img = new Image();
    await new Promise((resolve, reject) => {
      img.onload  = resolve;
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = url;
    });
    let w = img.naturalWidth;
    let h = img.naturalHeight;
    if (!w || !h) {
      // SVG-only fallback - some browsers leave naturalWidth/Height at 0
      // when the SVG declares only a viewBox.
      try {
        const head = await blob.slice(0, 2048).text();
        const m = head.match(/viewBox=["']\s*[\d.-]+\s+[\d.-]+\s+([\d.]+)\s+([\d.]+)\s*["']/i);
        if (m) { w = parseFloat(m[1]); h = parseFloat(m[2]); }
      } catch {}
    }
    if (!w || !h) { w = 1024; h = 1024; }
    const scale = Math.min(1, maxDim / Math.max(w, h));
    const cw = Math.max(256, Math.round(w * scale));
    const ch = Math.max(256, Math.round(h * scale));
    const canvas = document.createElement('canvas');
    canvas.width = cw;
    canvas.height = ch;
    const ctx = canvas.getContext('2d');
    // Flatten transparent areas onto white before JPG encoding.
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, cw, ch);
    ctx.drawImage(img, 0, 0, cw, ch);
    return await new Promise((resolve, reject) => {
      canvas.toBlob(out => {
        if (out) resolve(out);
        else reject(new Error('Canvas → JPG encoding failed'));
      }, 'image/jpeg', quality);
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

// Authenticated download that fetches an output URL, rasterizes the
// returned blob (SVG or PNG) to a JPG client-side, and triggers the
// browser save dialog. Used by the "Download as JPG" secondary download
// button on vector + transparent-PNG outputs.
export async function authDownloadAsJpg(url, filename) {
  try {
    const user = auth.currentUser;
    if (!user) return;
    const token = await user.getIdToken();
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error('Image download failed');
    const blob = await res.blob();
    const jpgBlob = await rasterizeBlobToJpg(blob, { maxDim: 2048 });
    const blobUrl = URL.createObjectURL(jpgBlob);
    const a = document.createElement('a');
    a.href = blobUrl; a.download = filename;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  } catch (err) {
    console.error('Image → JPG download error:', err);
  }
}

/** Resolution pillbox — always uses themed color. Light mode renders
 *  a thicker border + heavier font weight so the pill stays legible
 *  on the saturated yellow page (the 1px / weight-700 from dark mode
 *  was washing out). */
export const ResPill = ({ res, small, large }) => {
  if (!res) return null;
  const c = resColor(res);
  const label = resLabel(res);
  const fs = large ? 10 : small ? 8 : 9;
  const px = large ? 7 : small ? 4 : 6;
  const py = large ? 3 : small ? 1 : 2;
  const radius = large ? 12 : 8;
  const isLight = T._mode === "light";
  const borderWidth = isLight ? 2 : 1;
  const fontWeight  = isLight ? 800 : 700;
  return (
    <span style={{
      display:"inline-flex",alignItems:"center",justifyContent:"center",
      border:`${borderWidth}px solid ${c}`,borderRadius:radius,
      padding:`${py}px ${px}px`,
      color:c,fontSize:fs,fontWeight,lineHeight:1,
      whiteSpace:"nowrap",flexShrink:0,
    }}>{label}</span>
  );
};

export const LeopardBg = ({ offset = 0, animating = false }) => (
  <div style={{
    position:"fixed", inset:"-5%", pointerEvents:"none", zIndex:0,
    backgroundImage:`url(${BG_SRC})`,
    backgroundSize:"cover", backgroundPosition:"center", backgroundRepeat:"no-repeat",
    opacity:0.2,
    transform:`translateY(${offset * 0.15}px)`,
    transition: "none",
    willChange:"transform",
  }}/>
);

export const Logo = ({ size = 32 }) => (
  <img src="/logo.svg" alt="Katana AI" style={{width:size, height:"auto", display:"block", flexShrink:0}}/>
);

export const KlingLogo = () => (
  // Same nested chassis as KlingLogoShared so the small fixed-20px
  // variant matches Grok's geometry in light mode (small inner black
  // square on a faint outer wrapper, not a full-bleed black square).
  <div style={{width:20,height:20,borderRadius:4,background:T.modelBadgeBg,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,overflow:"hidden"}}>
    <div style={{width:14,height:14,borderRadius:3,background:"#000",display:"flex",alignItems:"center",justifyContent:"center"}}>
      <img src="/kling-logo.png" style={{width:"100%",height:"100%",objectFit:"contain"}} alt="Kling"/>
    </div>
  </div>
);

export const Pill = ({active, onClick, title:t, w, children}) => (
  <button onClick={onClick} title={t} style={{...(w ? {width:w} : {paddingLeft:10,paddingRight:10}),height:30,borderRadius:20,
    background:active?Y:"transparent",
    border:`1px solid ${active?Y:T.overlay12}`,
    color:active?T.dark:T.text3,fontWeight:active?800:400,
    fontSize:12,cursor:"pointer",transition:"all 0.15s",
    display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
    {children}
  </button>
);

// Per-provider prompt character limits. Hard-enforced by the prompt textareas
// (native maxLength) with a counter that warns as the cap is reached. Sources:
// Kling 2500 (provider 400 + docs), Recraft v3=1000 / v4=10000, Veo
// ~1024-token (~2000 chars). Grok / BFL / Nano-Banana publish no hard cap,
// so these are defensive ceilings.
export const PROMPT_LIMITS = {
  kling:      { prompt: 2500,  negative: 2500 },
  veo:        { prompt: 2000,  negative: 2000 },
  nanoBanana: { prompt: 5000,  negative: 5000 },
  grok:       { prompt: 2000,  negative: 2000 },
  bfl:        { prompt: 5000,  negative: 5000 },
  recraftV3:  { prompt: 1000,  negative: 1000 },
  recraftV4:  { prompt: 10000, negative: 10000 },
  openai:     { prompt: 32000, negative: 0 },
};

// Limit for the active VIDEO model (MODELS entry id).
export function videoPromptLimit(model) {
  const fam = !model ? 'kling'
    : model.startsWith('grok') ? 'grok'
    : model.startsWith('google-') ? 'veo'
    : 'kling';
  return PROMPT_LIMITS[fam];
}

// Limit for the active IMAGE model (IMAGE_MODELS entry with flavor + id).
export function imagePromptLimit(imgSel) {
  if (!imgSel) return PROMPT_LIMITS.kling;
  const id = imgSel.id || '';
  switch (imgSel.flavor) {
    case 'grok':    return PROMPT_LIMITS.grok;
    case 'bfl':     return PROMPT_LIMITS.bfl;
    case 'recraft': return id.startsWith('recraft-v4') ? PROMPT_LIMITS.recraftV4 : PROMPT_LIMITS.recraftV3;
    case 'google':  return PROMPT_LIMITS.nanoBanana;
    case 'openai':  return PROMPT_LIMITS.openai;
    default:        return PROMPT_LIMITS.kling; // omni / gen / multi
  }
}

export const FocusTextarea = React.forwardRef(({value, onChange, title, style, rows, placeholder, maxLength}, fwdRef) => {
  const [focused, setFocused] = useState(false);
  const innerRef = useRef(null);
  // Expose the DOM node to parent refs (callable or object refs).
  React.useImperativeHandle(fwdRef, () => innerRef.current, []);
  const borderColor = focused ? Y : value ? T.title : T.inputBorder;
  const autoSize = useCallback(() => {
    const el = innerRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = el.scrollHeight + 'px';
  }, []);
  useEffect(() => { autoSize(); }, [value, autoSize]);
  useEffect(() => { window.addEventListener('resize', autoSize); return () => window.removeEventListener('resize', autoSize); }, [autoSize]);
  // Convert height to minHeight so auto-sizing works
  const { height, ...rest } = style || {};
  const merged = { ...rest, minHeight: height || undefined, overflow: 'hidden' };
  const ta = (
    <textarea ref={innerRef} value={value} onChange={onChange} title={title} rows={rows} placeholder={placeholder} maxLength={maxLength}
      onFocus={()=>setFocused(true)} onBlur={()=>setFocused(false)}
      style={{...merged, borderBottom:`1px solid ${borderColor}`, transition:"border-color 0.2s"}}
    />
  );
  // No limit -> render bare (preserves callers that don't pass maxLength).
  if (!maxLength) return ta;
  const len = (value || '').length;
  // maxLength hard-caps input, so len can only reach (not exceed) the cap;
  // pink = at the limit, yellow = approaching, muted otherwise.
  const counterColor = len >= maxLength ? '#FF6FB5' : len >= maxLength * 0.9 ? Y : T.text3;
  return (
    <div>
      {ta}
      <div style={{fontSize:11,textAlign:'right',color:counterColor,marginTop:2,fontFamily:'monospace',letterSpacing:0.3}}>{len}/{maxLength}</div>
    </div>
  );
});

export const CopyBtn = ({text}) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    try {
      const el = document.createElement('textarea');
      el.value = text;
      el.style.cssText = 'position:fixed;opacity:0;top:0;left:0';
      document.body.appendChild(el);
      el.focus(); el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(()=>setCopied(false), 1500);
    } catch(e) {
      navigator.clipboard?.writeText(text).then(()=>{setCopied(true);setTimeout(()=>setCopied(false),1500);});
    }
  };
  return (
    <button onClick={copy} title="Copy to clipboard"
      style={{background:"transparent",border:"none",color:copied?Y:T.text3,cursor:"pointer",padding:"2px 4px",borderRadius:6,display:"flex",alignItems:"center",transition:"color 0.2s"}}>
      {copied
        ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
        : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
      }
    </button>
  );
};

export const StatusIcon = ({status, size=14}) => {
  if (status==="ready")      return <svg width={size} height={size} viewBox="0 0 24 24" fill="#EEFF44"><polygon points="5 3 19 12 5 21 5 3"/></svg>;
  if (status==="processing") return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#3DD6C8" strokeWidth="2.5" strokeLinecap="round"><path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/></svg>;
  if (status==="queued")     return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#415459" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>;
  return                            <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={T.danger} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>;
};

export const RatioSymbol = ({ratio}) => {
  const s = {fontSize:11, color:"#415459"};
  if(ratio==="16:9") return <span style={s} title="16:9">▬</span>;
  if(ratio==="9:16") return <span style={s} title="9:16">▮</span>;
  if(ratio==="1:1")  return <span style={s} title="1:1">■</span>;
  return null;
};

export const ModelBadge = ({model}) => (
  <div style={{width:14,height:14,borderRadius:2,background:"rgba(255,255,255,0.08)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}} title={model||"Kling"}>
    <svg width="8" height="8" viewBox="0 0 10 10" fill="none"><polygon points="2,1 8,5 2,9" fill={T.text3}/></svg>
  </div>
);

export const GrokLogoShared = ({s=20}) => (
  <div style={{width:s,height:s,borderRadius:4,background:T.modelBadgeBg,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,overflow:"hidden"}}>
    <svg width={s*0.7} height={s*0.7} viewBox="0 0 512 510" fill="#fff"><path d="M115.6 0h280.8C459.97 0 512 52 512 115.6v278.4c0 63.6-52 115.6-115.6 115.6H115.6C52 509.6 0 457.6 0 394V115.6C0 52 52 0 115.6 0z" fill="#000"/><path d="M213.2 306l179-180v.2l51.7-51.8c-.9 1.3-1.9 2.6-2.8 3.9-39.3 54.2-58.5 80.6-43.1 146.9l-.1-.1c10.6 45.1-.7 95.1-37.4 131.8-46.2 46.3-120.2 56.6-181.1 14.9l42.5-19.7c38.9 15.3 81.4 8.6 111.9-22 30.6-30.6 37.4-75.2 22.1-112.3-2.9-7-11.7-8.8-17.8-4.3L213.2 306zm-25.8 22.4l-.1.1L68.1 435.2c7.6-10.4 17-20.3 26.3-30.1 26.4-27.8 52.7-55.4 36.7-94.3-21.4-52.1-9-113.2 30.7-152.9 41.2-41.3 102-51.7 152.7-30.8 11.2 4.2 21 10.1 28.6 15.6l-42.4 19.6c-39.4-16.6-84.6-5.3-112.2 22.3-37.3 37.3-44.8 102-1.1 143.8z" fill="#fff"/></svg>
  </div>
);

export const KlingLogoShared = ({s=20}) => (
  // Two-layer chassis to mirror Grok exactly: outer wrapper uses the
  // theme-aware T.modelBadgeBg (matches every other model badge), and an
  // inner 70%-sized solid-black square holds the PNG. In dark mode both
  // layers are black so the badge looks unified; in light mode the outer
  // 15%-black fades back and only the small inner black square shows -
  // same visual footprint as GrokLogoShared, whose SVG paints its black
  // chassis at 70% of the wrapper.
  <div style={{width:s,height:s,borderRadius:Math.max(4,s*0.15),background:T.modelBadgeBg,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,overflow:"hidden"}}>
    <div style={{width:s*0.7,height:s*0.7,borderRadius:Math.max(3,s*0.1),background:"#000",display:"flex",alignItems:"center",justifyContent:"center"}}>
      <img src="/kling-logo.png" style={{width:"100%",height:"100%",objectFit:"contain"}} alt="Kling"/>
    </div>
  </div>
);

// Gemini brand mark - the official Google asset (sourced from
// Wikimedia Commons File:Google_Gemini_icon_2025.svg, public domain
// per "below the threshold of originality"; trademark still applies
// but identifying a Gemini-powered feature with Google's own mark is
// the standard third-party usage). Used for every model served via
// Vertex AI / Gemini API: Imagen 4 today, Nano Banana and Veo later.
export const GeminiLogoShared = ({s=20}) => (
  <div style={{width:s,height:s,borderRadius:Math.max(4,s*0.15),background:T.modelBadgeBg,display:"flex",alignItems:"center",justifyContent:"center",padding:s*0.16,flexShrink:0,overflow:"hidden"}}>
    <img src="/gemini-logo.svg" style={{width:"100%",height:"100%",objectFit:"contain"}} alt="Gemini"/>
  </div>
);

// Recraft brand mark. The /recraft-logo.svg used to bake its rounded
// black chassis into the SVG itself; we now wrap it in a div with
// T.modelBadgeBg so the chassis is theme-aware (solid black in dark,
// 15% black in light - matches the other model badges).
export const RecraftLogoShared = ({s=20}) => (
  <div style={{width:s,height:s,borderRadius:Math.max(4,s*0.15),background:T.modelBadgeBg,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,overflow:"hidden"}}>
    <img src="/recraft-logo.svg" style={{width:"100%",height:"100%",objectFit:"contain"}} alt="Recraft"/>
  </div>
);

// Black Forest Labs brand mark. The /bfl-logo.svg is white-on-transparent,
// so we use the same nested two-layer chassis as Kling/Grok - a solid-black
// inner square keeps the white mark legible in light mode (where the outer
// T.modelBadgeBg fades back to 15% black).
export const BflLogoShared = ({s=20}) => (
  <div style={{width:s,height:s,borderRadius:Math.max(4,s*0.15),background:T.modelBadgeBg,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,overflow:"hidden"}}>
    <div style={{width:s*0.7,height:s*0.7,borderRadius:Math.max(3,s*0.1),background:"#000",display:"flex",alignItems:"center",justifyContent:"center",padding:s*0.08}}>
      <img src="/bfl-logo.svg" style={{width:"100%",height:"100%",objectFit:"contain"}} alt="Black Forest Labs"/>
    </div>
  </div>
);

// Neutral "GPT" text badge for OpenAI models; swap in the official mark
// (public/openai-logo.svg) if we add one.
export const OpenAILogoShared = ({s=20}) => (
  <div style={{width:s,height:s,borderRadius:4,background:T.modelBadgeBg,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,overflow:"hidden"}}>
    <div style={{width:s*0.78,height:s*0.78,borderRadius:Math.max(2,s*0.16),background:"#000",color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontSize:Math.max(6,s*0.27),fontWeight:800,letterSpacing:"-0.02em",fontFamily:"inherit"}}>GPT</div>
  </div>
);

export const ModelIcon = ({model, s=20}) => {
  if (model && model.startsWith("openai"))  return <OpenAILogoShared s={s}/>;
  if (model && model.startsWith("grok"))    return <GrokLogoShared s={s}/>;
  if (model && model.startsWith("google"))  return <GeminiLogoShared s={s}/>;
  if (model && model.startsWith("recraft")) return <RecraftLogoShared s={s}/>;
  if (model && model.startsWith("bfl"))     return <BflLogoShared s={s}/>;
  return <KlingLogoShared s={s}/>;
};

/* ── Katana Credits (app currency) ────────────────────────────────────────── */

/** Generation coin icon — katana-credit-3.svg */
export const CreditIcon = ({ size = 18, color = CREDIT, style: sx }) => (
  <svg width={size * 0.55} height={size * 0.55} viewBox="0 0 209.55005 209.54999" fill={color} style={{ display:"inline-block", verticalAlign:"middle", flexShrink:0, ...sx }}>
    <g transform="translate(-1180.3424,-500.41545)">
      <path d="m 1285.1174,500.41545 a 104.775,104.775 0 0 0 -104.775,104.775 104.775,104.775 0 0 0 104.775,104.775 104.775,104.775 0 0 0 104.775,-104.775 104.775,104.775 0 0 0 -104.775,-104.775 z m 0,46.30156 c 0.1799,0 0.3598,0.014 0.5375,0.0419 0.1776,0.0279 0.3529,0.0698 0.524,0.12557 0.171,0.0558 0.3375,0.12558 0.4976,0.20929 0.1601,0.0837 0.3137,0.18139 0.4584,0.29301 0.1447,0.11162 0.2807,0.23719 0.4056,0.37672 0.125,0.13953 0.2388,0.29301 0.3395,0.46044 0.1008,0.16743 0.1883,0.34881 0.2605,0.54415 0.072,0.19534 0.1292,0.40463 0.1685,0.62787 0.1463,0.83974 0.3088,1.67412 0.4868,2.50217 0.1779,0.82805 0.3713,1.64977 0.5803,2.46548 0.2089,0.81572 0.433,1.62554 0.6723,2.42828 0.2393,0.80274 0.4937,1.59885 0.7627,2.38797 0.2691,0.78911 0.5531,1.57126 0.8511,2.34611 0.2981,0.77485 0.6104,1.54224 0.9369,2.30218 0.3266,0.75994 0.6669,1.51233 1.0212,2.25671 0.3543,0.74439 0.7223,1.48098 1.1038,2.20917 0.3814,0.72819 0.7764,1.4477 1.1844,2.15904 0.4079,0.71135 0.8292,1.41403 1.263,2.10788 0.4338,0.69386 0.8804,1.37893 1.3394,2.05466 0.459,0.67573 0.9303,1.34241 1.4139,1.99936 0.4836,0.65695 0.9792,1.30446 1.4867,1.942 0.5075,0.63754 1.0268,1.2651 1.5575,1.88257 0.5308,0.61748 1.0734,1.22483 1.6268,1.8216 0.5534,0.59677 1.118,1.18313 1.6935,1.75855 0.5754,0.57541 1.1612,1.14001 1.758,1.69343 0.5967,0.55343 1.2041,1.09547 1.8216,1.62626 0.6174,0.53079 1.245,1.05002 1.8826,1.55753 0.6375,0.5075 1.285,1.00315 1.942,1.48673 0.6569,0.48358 1.3231,0.95537 1.9988,1.41438 0.6757,0.45901 1.3608,0.90514 2.0547,1.33894 0.6938,0.4338 1.3965,0.85503 2.1078,1.26297 0.7114,0.40794 1.4314,0.80299 2.1596,1.18442 0.7282,0.38144 1.4648,0.74952 2.2092,1.10381 0.7443,0.3543 1.4962,0.69462 2.2562,1.02113 0.7599,0.3265 1.5278,0.63882 2.3027,0.93689 0.7748,0.29808 1.557,0.58212 2.3461,0.85111 0.7891,0.269 1.5852,0.52296 2.3879,0.76223 0.8028,0.23928 1.6121,0.46392 2.4278,0.67283 0.8157,0.20891 1.6374,0.40242 2.4655,0.58033 0.828,0.1779 1.6624,0.34002 2.5022,0.48627 0.2232,0.0393 0.4325,0.0962 0.6278,0.16847 0.1954,0.0722 0.3767,0.15967 0.5442,0.26044 0.1674,0.10078 0.3209,0.21458 0.4604,0.33952 0.1395,0.12493 0.2651,0.26096 0.3767,0.40566 0.1116,0.1447 0.2093,0.2983 0.293,0.45837 0.084,0.16007 0.1535,0.32659 0.2093,0.49764 0.056,0.17105 0.098,0.34636 0.1256,0.524 0.028,0.17764 0.042,0.3576 0.042,0.53744 0,0.17983 -0.014,0.3598 -0.042,0.53743 -0.028,0.17764 -0.07,0.35295 -0.1256,0.524 -0.056,0.17105 -0.1256,0.33758 -0.2093,0.49764 -0.084,0.16007 -0.1814,0.31368 -0.293,0.45837 -0.1116,0.1447 -0.2372,0.28074 -0.3767,0.40566 -0.1395,0.12493 -0.293,0.23875 -0.4604,0.33952 -0.1675,0.10077 -0.3488,0.18823 -0.5442,0.26045 -0.1953,0.0722 -0.4046,0.12919 -0.6278,0.16846 -0.8398,0.1463 -1.6742,0.30834 -2.5022,0.48628 -0.8281,0.17794 -1.6498,0.3719 -2.4655,0.58084 -0.8157,0.20894 -1.6255,0.43301 -2.4283,0.67231 -0.8027,0.2393 -1.5983,0.49373 -2.3874,0.76275 -0.7891,0.26902 -1.5713,0.5525 -2.3461,0.85059 -0.7749,0.29809 -1.5428,0.61089 -2.3027,0.93741 -0.76,0.32652 -1.5124,0.66682 -2.2568,1.02113 -0.7443,0.3543 -1.4804,0.72236 -2.2086,1.10381 -0.7282,0.38144 -1.4482,0.77647 -2.1596,1.18442 -0.7113,0.40794 -1.414,0.82917 -2.1078,1.26297 -0.6939,0.4338 -1.379,0.87993 -2.0547,1.33894 -0.6757,0.45901 -1.3424,0.9308 -1.9994,1.41438 -0.6569,0.48358 -1.3044,0.97923 -1.942,1.48673 -0.6375,0.5075 -1.2651,1.02675 -1.8825,1.55753 -0.6175,0.53078 -1.2249,1.07284 -1.8216,1.62626 -0.5968,0.55341 -1.1832,1.11803 -1.7586,1.69343 -0.5754,0.57541 -1.14,1.1618 -1.6934,1.75855 -0.5534,0.59676 -1.0955,1.20414 -1.6263,1.8216 -0.5308,0.61746 -1.05,1.24505 -1.5575,1.88257 -0.5075,0.63752 -1.0031,1.28506 -1.4867,1.942 -0.4836,0.65694 -0.9554,1.32313 -1.4144,1.99884 -0.459,0.67572 -0.9051,1.36082 -1.3389,2.05466 -0.4338,0.69384 -0.8551,1.39655 -1.263,2.10788 -0.408,0.71133 -0.803,1.43139 -1.1844,2.15956 -0.3815,0.72817 -0.7496,1.46428 -1.1039,2.20865 -0.3542,0.74437 -0.6946,1.49678 -1.0211,2.25671 -0.3265,0.75993 -0.6388,1.52735 -0.9369,2.30218 -0.298,0.77484 -0.5821,1.55701 -0.8511,2.34611 -0.269,0.78911 -0.5229,1.58524 -0.7622,2.38797 -0.2393,0.80273 -0.4639,1.61205 -0.6728,2.42776 -0.2089,0.81571 -0.4025,1.63795 -0.5804,2.466 -0.1779,0.82805 -0.34,1.66191 -0.4862,2.50166 -0.039,0.22324 -0.096,0.43253 -0.1685,0.62787 -0.072,0.19533 -0.1597,0.37672 -0.2605,0.54415 -0.1007,0.16743 -0.2145,0.32091 -0.3395,0.46044 -0.1249,0.13952 -0.2609,0.2651 -0.4056,0.37672 -0.1447,0.11162 -0.2983,0.20929 -0.4584,0.293 -0.1601,0.0837 -0.3266,0.15348 -0.4976,0.20929 -0.1711,0.0558 -0.3464,0.0977 -0.524,0.12558 -0.1777,0.0279 -0.3576,0.0419 -0.5375,0.0419 -0.1798,0 -0.3598,-0.014 -0.5374,-0.0419 -0.1776,-0.0279 -0.353,-0.0698 -0.524,-0.12558 -0.1711,-0.0558 -0.3376,-0.12557 -0.4977,-0.20929 -0.16,-0.0837 -0.3136,-0.18138 -0.4583,-0.293 -0.1447,-0.11162 -0.2808,-0.2372 -0.4057,-0.37672 -0.1249,-0.13953 -0.2387,-0.29301 -0.3395,-0.46044 -0.1008,-0.16743 -0.1882,-0.34882 -0.2604,-0.54415 -0.072,-0.19534 -0.1292,-0.40463 -0.1685,-0.62787 -0.1463,-0.83975 -0.3083,-1.67361 -0.4863,-2.50166 -0.1779,-0.82805 -0.3719,-1.65028 -0.5808,-2.466 -0.209,-0.81572 -0.433,-1.62502 -0.6723,-2.42776 -0.2393,-0.80274 -0.4938,-1.59885 -0.7628,-2.38797 -0.269,-0.78911 -0.5525,-1.57126 -0.8506,-2.34611 -0.2981,-0.77484 -0.6109,-1.54224 -0.9374,-2.30218 -0.3265,-0.75994 -0.6668,-1.51233 -1.0211,-2.25671 -0.3543,-0.74438 -0.7224,-1.48098 -1.1038,-2.20917 -0.3815,-0.72818 -0.7765,-1.44769 -1.1844,-2.15904 -0.408,-0.71134 -0.8292,-1.41454 -1.263,-2.1084 -0.4338,-0.69386 -0.8799,-1.37841 -1.339,-2.05414 -0.459,-0.67572 -0.9308,-1.3424 -1.4143,-1.99936 -0.4836,-0.65695 -0.9793,-1.30446 -1.4868,-1.942 -0.5075,-0.63753 -1.0267,-1.2651 -1.5575,-1.88257 -0.5308,-0.61747 -1.0728,-1.22483 -1.6263,-1.82159 -0.5534,-0.59677 -1.118,-1.18313 -1.6934,-1.75855 -0.5754,-0.57542 -1.1618,-1.14001 -1.7585,-1.69344 -0.5968,-0.55342 -1.2042,-1.09547 -1.8216,-1.62626 -0.6175,-0.53078 -1.2451,-1.05002 -1.8826,-1.55752 -0.6375,-0.50751 -1.2851,-1.00315 -1.942,-1.48674 -0.6569,-0.48358 -1.3231,-0.95537 -1.9989,-1.41438 -0.6757,-0.45901 -1.3608,-0.90565 -2.0546,-1.33945 -0.6939,-0.4338 -1.3966,-0.85452 -2.1079,-1.26246 -0.7113,-0.40794 -1.4314,-0.80298 -2.1595,-1.18442 -0.7282,-0.38144 -1.4643,-0.74952 -2.2087,-1.10381 -0.7444,-0.35429 -1.4968,-0.69462 -2.2567,-1.02113 -0.7599,-0.3265 -1.5274,-0.63882 -2.3022,-0.93689 -0.7748,-0.29807 -1.557,-0.58212 -2.3461,-0.85111 -0.7891,-0.269 -1.5852,-0.52295 -2.388,-0.76223 -0.8027,-0.23927 -1.612,-0.46392 -2.4277,-0.67283 -0.8157,-0.2089 -1.638,-0.40242 -2.466,-0.58032 -0.8281,-0.1779 -1.6619,-0.34003 -2.5017,-0.48628 -0.2232,-0.0393 -0.4325,-0.0962 -0.6279,-0.16846 -0.1953,-0.0722 -0.3767,-0.15967 -0.5441,-0.26045 -0.1674,-0.10078 -0.3209,-0.21458 -0.4604,-0.33952 -0.1396,-0.12493 -0.2651,-0.26096 -0.3768,-0.40566 -0.1116,-0.14469 -0.2093,-0.29829 -0.293,-0.45837 -0.084,-0.16007 -0.1535,-0.32659 -0.2093,-0.49764 -0.056,-0.17105 -0.098,-0.34688 -0.1255,-0.52452 -0.028,-0.17763 -0.042,-0.35708 -0.042,-0.53691 0,-0.17984 0.014,-0.3598 0.042,-0.53744 0.028,-0.17763 0.07,-0.35295 0.1255,-0.524 0.056,-0.17104 0.1256,-0.33758 0.2093,-0.49764 0.084,-0.16007 0.1814,-0.31368 0.293,-0.45837 0.1117,-0.14469 0.2372,-0.28074 0.3768,-0.40566 0.1395,-0.12493 0.293,-0.23875 0.4604,-0.33952 0.1674,-0.10076 0.3488,-0.18823 0.5441,-0.26044 0.1954,-0.0722 0.4047,-0.1292 0.6279,-0.16847 0.8398,-0.14629 1.6736,-0.30885 2.5017,-0.48679 0.828,-0.17794 1.6503,-0.37138 2.466,-0.58033 0.8157,-0.20894 1.625,-0.43301 2.4277,-0.67231 0.8028,-0.2393 1.5989,-0.49372 2.388,-0.76274 0.7891,-0.26902 1.5713,-0.55302 2.3461,-0.85111 0.7749,-0.2981 1.5423,-0.61038 2.3022,-0.9369 0.7599,-0.32652 1.5123,-0.66682 2.2567,-1.02112 0.7444,-0.35431 1.481,-0.72236 2.2092,-1.10381 0.7282,-0.38145 1.4477,-0.77648 2.159,-1.18443 0.7114,-0.40794 1.4145,-0.82917 2.1084,-1.26297 0.6939,-0.4338 1.3784,-0.88044 2.0541,-1.33945 0.6758,-0.45901 1.3425,-0.93029 1.9994,-1.41387 0.657,-0.48358 1.3045,-0.97923 1.942,-1.48673 0.6375,-0.5075 1.2651,-1.02675 1.8826,-1.55753 0.6175,-0.53078 1.2248,-1.07336 1.8216,-1.62677 0.5967,-0.55342 1.1831,-1.11751 1.7585,-1.69292 0.5754,-0.57541 1.14,-1.16179 1.6935,-1.75855 0.5534,-0.59676 1.0954,-1.20413 1.6262,-1.82159 0.5308,-0.61746 1.05,-1.24505 1.5575,-1.88258 0.5075,-0.63752 1.0032,-1.28506 1.4868,-1.942 0.4836,-0.65694 0.9553,-1.32313 1.4144,-1.99884 0.459,-0.67571 0.9056,-1.36081 1.3394,-2.05466 0.4338,-0.69384 0.8545,-1.39655 1.2625,-2.10788 0.4079,-0.71133 0.803,-1.43138 1.1844,-2.15956 0.3814,-0.72817 0.7495,-1.46479 1.1038,-2.20916 0.3543,-0.74438 0.6946,-1.49627 1.0211,-2.2562 0.3265,-0.75992 0.6388,-1.52786 0.9369,-2.3027 0.2981,-0.77484 0.5821,-1.557 0.8511,-2.34611 0.269,-0.78911 0.523,-1.58472 0.7623,-2.38745 0.2392,-0.80273 0.4639,-1.61256 0.6728,-2.42828 0.2089,-0.81571 0.4024,-1.63743 0.5803,-2.46548 0.1779,-0.82805 0.34,-1.66243 0.4863,-2.50217 0.039,-0.22324 0.096,-0.43253 0.1685,-0.62787 0.072,-0.19534 0.1596,-0.37672 0.2604,-0.54415 0.1008,-0.16743 0.2146,-0.32091 0.3395,-0.46044 0.1249,-0.13953 0.261,-0.2651 0.4057,-0.37672 0.1447,-0.11162 0.2983,-0.20929 0.4583,-0.29301 0.1601,-0.0837 0.3266,-0.15347 0.4977,-0.20929 0.171,-0.0558 0.3469,-0.0977 0.5245,-0.12557 0.1776,-0.0279 0.3571,-0.0419 0.5369,-0.0419 z"/>
    </g>
  </svg>
);

/**
 * Download a video in a way that works well across devices.
 *
 * Flow:
 *  1. Call the backend with `?download=true` to get a short-lived signed GCS URL.
 *  2. On iOS (where the <a download> attribute is ignored for cross-origin URLs),
 *     fetch the video as a blob and hand it to the Web Share API as a File.
 *     iOS then shows a native share sheet with "Save Video", which saves directly
 *     to the Photos app.
 *  3. On desktop and Android, use the standard <a href download> click.
 *
 * `onStatus` is an optional callback invoked with one of:
 *   "fetching" - download has started (useful for a spinner)
 *   "sharing"  - native share sheet is open (iOS only)
 *   "done"     - finished (successfully or user dismissed share sheet)
 *   "error"    - something went wrong; receives an Error as second arg
 */
export async function downloadVideo(url, filename, onStatus) {
  const status = (s, err) => { try { onStatus && onStatus(s, err); } catch {} };
  try {
    const user = auth.currentUser;
    if (!user) { status("error", new Error("Not signed in")); return; }
    const token = await user.getIdToken();
    const dlUrl = url + (url.includes('?') ? '&' : '?') + 'download=true';
    status("fetching");
    const res = await fetch(dlUrl, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) { status("error", new Error(`HTTP ${res.status}`)); return; }
    const data = await res.json();
    if (!data.url) { status("error", new Error("No download URL")); return; }

    const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
    // iPad on iOS 13+ reports as Mac, so also check maxTouchPoints
    const isIOS = /iPad|iPhone|iPod/.test(ua)
      || (ua.includes("Mac") && typeof navigator !== "undefined" && navigator.maxTouchPoints > 1);

    if (isIOS && typeof navigator !== "undefined" && navigator.canShare && navigator.share) {
      // Fetch the video bytes so we can share as a File (the path that unlocks
      // "Save Video" -> Photos in the iOS share sheet).
      try {
        const vidRes = await fetch(data.url);
        if (!vidRes.ok) throw new Error(`Video fetch ${vidRes.status}`);
        const blob = await vidRes.blob();
        const file = new File([blob], filename, { type: blob.type || "video/mp4" });
        if (navigator.canShare({ files: [file] })) {
          status("sharing");
          try {
            await navigator.share({ files: [file], title: "Katana AI video" });
          } catch (shareErr) {
            // User dismissed / cancelled share sheet — treat as a clean finish.
            if (shareErr && shareErr.name !== "AbortError") throw shareErr;
          }
          status("done");
          return;
        }
      } catch (iosErr) {
        // Fall through to the <a download> path below as a best-effort fallback.
        console.warn("iOS share fallback:", iosErr);
      }
    }

    // Desktop / Android / iOS-fallback: standard anchor click.
    const a = document.createElement('a');
    a.href = data.url;
    a.download = filename;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    status("done");
  } catch (err) {
    status("error", err);
  }
}

/** Formatted credit amount with icon, e.g. [coin]2,500 */
export const CreditAmount = ({ amount, size = 14, style: sx }) => (
  <span style={{
    color: CREDIT, fontWeight: 900, fontSize: size,
    fontFamily: "Mulish, sans-serif",
    letterSpacing: "-0.01em",
    lineHeight: 1,
    display: "inline-flex", alignItems: "center", gap: size * 0.15,
    ...sx,
  }}>
    <CreditIcon size={size * 0.7} color={CREDIT}/>
    {typeof amount === "number" ? amount.toLocaleString() : amount}
  </span>
);
